<template>
  <div>

    <accounting-links v-if="isReportPage"></accounting-links>

    <div class="box" :class="{ 'py-1 px-1': compact }">
      <div class="flex flex-wrap items-center" v-if="showActions">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getReceipts" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <button
            v-if="hasPermission('receipt.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.receipt.enabled"
            @click="exportReceipts"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.receipt.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getReceipts">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>

      </div>

      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <receipt-filters
            :filters.sync="filters"
            @updated="getReceipts"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasReceipts">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th v-if="!compact">#</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Method</th>
                  <th>Created at</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(receipt, i) in receipts" :key="i">
                  <td class="font-bold" v-if="!compact">{{ receipt.id }}</td>
                  <td>
                    <a class="text-link" @click.stop="$router.push($contactPageByType(receipt.contact, 'details'))">
                      {{ receipt.contact.first_name }}
                      {{ receipt.contact.last_name }}
                      <span v-if="!compact && receipt.contact.company_name">
                        ({{ receipt.contact.company_name }})
                      </span>
                    </a>
                  </td>
                  <td>
                    <span :class="receipt.typeClass">{{ receipt.type }}</span>
                  </td>
                  <td>{{ $formatMoney(receipt.payment.value) }}</td>
                  <td>{{ receipt.payment.method }}</td>
                  <td>
                    {{ $moment(receipt.created_at).format(config.dateTimeFormats.display) }}
                  </td>
                  <td>
                    <a
                      class="hover:text-theme-darker text-base cursor-pointer p-2"
                      @click.stop="$router.push('/receipt/details/' + receipt.id)"
                    ><i class="fas fa-info"></i></a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasReceipts && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>


  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const AccountingLinks = resolve => require(['@/components/pages/reports/AccountingLinks'], resolve)
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const ReceiptFilters = resolve => require(['@/components/filters/Filters'], resolve)

export default {
  props: {
    showActions: {
      type: Boolean,
      default: true,
    },
    method: String,
    compact: Boolean,
  },
  components: {
    FilteringToggleButton,
    AccountingLinks,
    ListSearch,
    PaginationComponent,
    ReceiptFilters,
  },
  data() {
    return {
      isReportPage: false,
      receipts: [],
      searchInput: '',
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        type: {
          key: 'type',
          name: 'Type',
          active: true,
          options: [
            {value: 'sales', class: 'btn-green'},
            {value: 'purchases', class: 'btn-red'},
          ],
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      exportFile: {
        receipt: {
          enabled: true,
        }
      },
      query: {
        search: '',
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getReceipts()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getReceipts()
      }
    },
  },
  computed: {
    hasReceipts() {
      return Object.entries(this.receipts).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      isAdmin: 'user/isAdmin',
      hasPermission: 'user/hasPermission',
    })
  },
  created() {
    this.setRouteFilters()
  },
  mounted() {
    if (this.$route.name.split('.')[0] === 'report') {
      this.isReportPage = true
    }
    this.getReceipts()
  },
  methods: {
    _setRouteFilters(routeFilters) {
      for (let name in routeFilters) {
        for (let i in this.filters) {
          if (this.filters[i].key === name) {
            Object.assign(this.filters[i], routeFilters[name])
          }
        }
        if (routeFilters.children) {
          this._setRouteFilters(routeFilters.children)
        }
      }
    },
    setRouteFilters() {
      if (!this.$route.meta.filters) {
        return
      }
      let routeFilters = this.$route.meta.filters
      this._setRouteFilters(routeFilters)
    },
    exportReceipts() {
      this.exportFile.receipt.enabled = false
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      axios.get('receipts/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.receipt.enabled = true
        })
    },

    getReceipts() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      axios.get('receipts', {params: {
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.receipts = Object.assign([], res.data)
            this.receipts.map(p => {
              if (p.type === 'sales') {
                p.typeClass = 'text-green'
              } else if (p.type === 'purchases') {
                p.typeClass = 'text-red-500'
              }
            })

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
